import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button, Modal, Input } from "@/components/ui-kit";
import { Pencil, Trash2, Save, X } from "lucide-react";
import { formatQty, parseQty } from "@/lib/format";

/**
 * Damage History Modal — list of all damage reports for a single stock.
 * Allows inline edit qty/chronology and full delete.
 * Backend endpoints PATCH /api/damage-reports/{id} + DELETE will adjust stock.quantity
 * and stock.damaged_qty atomically.
 */
export function DamageHistoryModal({ open, onClose, stock, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [editChrono, setEditChrono] = useState("");

  const load = async () => {
    if (!stock) return;
    setLoading(true);
    try {
      const r = await api.get("/damage-reports", { params: { stock_id: stock.id } });
      setRows(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, stock?.id]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditQty(String(row.quantity));
    setEditChrono(row.chronology || "");
  };
  const cancelEdit = () => { setEditingId(null); setEditQty(""); setEditChrono(""); };

  const saveEdit = async (row) => {
    const newQty = parseQty(editQty);
    if (newQty < 0) { toast.error("Jumlah tidak boleh negatif"); return; }
    if (!editChrono.trim()) { toast.error("Kronologi tidak boleh kosong"); return; }
    try {
      await api.patch(`/damage-reports/${row.id}`, { quantity: newQty, chronology: editChrono.trim() });
      toast.success("Laporan diperbarui · stok disesuaikan");
      cancelEdit();
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui");
    }
  };

  const delRow = async (row) => {
    if (!window.confirm(`Hapus laporan ini? Stok akan dikembalikan +${row.quantity} ${stock.unit}.`)) return;
    try {
      await api.delete(`/damage-reports/${row.id}`);
      toast.success(`Laporan dihapus · stok dikembalikan +${row.quantity}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  if (!stock) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Riwayat Kerusakan — ${stock.material_name}`} size="xl">
      <div className="space-y-3">
        <div className="text-xs text-stone-500">
          Stok saat ini: <b>{formatQty(stock.quantity)} {stock.unit}</b> · Rusak bulan ini: <b className="text-red-700">{formatQty(stock.damaged_qty || 0)} {stock.unit}</b>
        </div>
        {loading && <p className="text-xs text-stone-400">Memuat…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-xs text-stone-400 text-center py-8">Belum ada laporan kerusakan untuk stok ini.</p>
        )}
        <div className="border border-stone-200 max-h-[60vh] overflow-y-auto">
          {rows.map((r, idx) => {
            const isEditing = editingId === r.id;
            return (
              <div key={r.id} className="border-b border-stone-100 last:border-b-0 px-3 py-3" data-testid={`damage-history-${idx}`}>
                <div className="flex items-start gap-3">
                  {r.photo_file_id && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo_file_id}`} target="_blank" rel="noopener noreferrer" className="shrink-0">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo_file_id}`}
                        alt="Foto"
                        className="h-20 w-20 object-cover border border-stone-200"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </a>
                  )}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 text-[11px] text-stone-500 font-mono">
                      <span>{r.ts?.slice(0, 16).replace("T", " ")} WIB</span>
                      {r.reported_by_name && <span>· oleh {r.reported_by_name}</span>}
                      {r.edited_at && <span className="italic">(diedit {r.edited_at.slice(0, 16).replace("T", " ")})</span>}
                    </div>
                    {isEditing ? (
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          value={editQty}
                          onChange={(e) => setEditQty(e.target.value)}
                          className="font-mono-num max-w-xs"
                          data-testid={`damage-edit-qty-${idx}`}
                        />
                        <textarea
                          rows={2}
                          value={editChrono}
                          onChange={(e) => setEditChrono(e.target.value)}
                          className="w-full border border-stone-300 px-2 py-1 text-sm"
                          data-testid={`damage-edit-chrono-${idx}`}
                        />
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-stone-900">
                          <b className="text-red-700 font-mono-num">{formatQty(r.quantity)} {stock.unit}</b>
                          <span className="text-stone-500 ml-1">rusak</span>
                        </div>
                        <div className="text-xs text-stone-700 whitespace-pre-wrap">{r.chronology}</div>
                      </>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {isEditing ? (
                      <>
                        <button onClick={() => saveEdit(r)} className="text-emerald-700 hover:text-emerald-900 p-1" title="Simpan" data-testid={`damage-save-${idx}`}><Save className="h-4 w-4" /></button>
                        <button onClick={cancelEdit} className="text-stone-400 hover:text-stone-700 p-1" title="Batal"><X className="h-4 w-4" /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(r)} className="text-stone-500 hover:text-terra-700 p-1" title="Edit" data-testid={`damage-edit-${idx}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => delRow(r)} className="text-stone-500 hover:text-red-700 p-1" title="Hapus" data-testid={`damage-delete-${idx}`}><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end pt-3 border-t border-stone-200">
          <Button variant="ghost" onClick={onClose}>Tutup</Button>
        </div>
      </div>
    </Modal>
  );
}
